import React from "react";
import { LangLink as Link } from "@/components/LangLink";
import { ArrowRight, Calendar, Repeat2, MessageCircle, Sparkles, CreditCard, Receipt, KeyRound, BarChart3, Globe2 } from "lucide-react";
import SEO from "@/components/SEO";
import { useT } from "@/hooks/useT";

/* ─── Channel brand chips for the marquee ─── */
const CHANNEL_BRANDS = [
  { name: "Booking.com",   bg: "#003580", mark: <span className="font-display text-[15px] font-extrabold text-white">B<span className="text-[#FEBA02]">.</span></span> },
  { name: "Airbnb",        bg: "#FF5A5F", mark: (
      <svg viewBox="0 0 32 32" className="h-5 w-5 fill-white" aria-hidden>
        <path d="M16 2c-3 0-5 2.2-6.8 5.4C5.1 14.7 2 21.7 2 25c0 3 2.2 5 5 5 2.4 0 4.7-1.6 9-7 4.3 5.4 6.6 7 9 7 2.8 0 5-2 5-5 0-3.3-3.1-10.3-7.2-17.6C20 4.2 18 2 16 2z" />
      </svg>
    ) },
  { name: "Expedia",       bg: "#FFC72C", mark: <span className="font-display text-[12px] font-extrabold text-[#191E3B]">EX</span> },
  { name: "Agoda",         bg: "#5392F9", mark: (
      <svg viewBox="0 0 32 32" className="h-5 w-5" aria-hidden>
        <circle cx="14" cy="14" r="9" fill="#fff" />
        <circle cx="20" cy="20" r="4" fill="#FF6F00" />
      </svg>
    ) },
  { name: "Google Hotels", bg: "#FFFFFF", border: "#E2E8F0", mark: (
      <svg viewBox="0 0 32 32" className="h-5 w-5" aria-hidden>
        <path fill="#4285F4" d="M16 14v4.4h6.2c-.3 1.4-1 2.6-2.1 3.4l3.5 2.7c2-1.9 3.2-4.6 3.2-7.8 0-.7-.1-1.5-.2-2.2H16z" />
        <path fill="#34A853" d="M9 18.7l-3.6 2.9C7.2 25.3 11.3 28 16 28c2.7 0 4.9-.9 6.6-2.4l-3.5-2.7c-1 .6-2.2 1-3.1 1-2.4 0-4.5-1.6-5.2-3.8z" />
        <path fill="#FBBC05" d="M5.4 10.4C4.5 12.1 4 14 4 16s.5 3.9 1.4 5.6L9 18.7c-.2-.6-.4-1.3-.4-2s.2-1.4.4-2L5.4 10.4z" />
        <path fill="#EA4335" d="M16 8.9c1.5 0 2.8.5 3.9 1.5l2.9-2.9C20.9 5.7 18.7 4.8 16 4.8c-4.7 0-8.8 2.7-10.6 6.6L9 14.3c.7-2.2 2.8-3.8 5.2-3.8z" />
      </svg>
    ) },
  { name: "Vrbo",          bg: "#245ABF", mark: <span className="font-display text-[11px] font-extrabold text-white">vrbo</span> },
  { name: "Hotelbeds",     bg: "#0E2A47", mark: <span className="font-display text-[11px] font-extrabold text-[#FFB400]">hb</span> },
  { name: "TripAdvisor",   bg: "#34E0A1", mark: <span className="font-display text-[14px] font-extrabold text-[#0F172A]">◎</span> },
];

const CHANNEL_BRANDS_2 = [
  { name: "Trivago",       bg: "#E10E49", mark: <span className="font-display text-[11px] font-extrabold text-white">trv</span> },
  { name: "Despegar",      bg: "#FF7F00", mark: <span className="font-display text-[14px] font-extrabold text-white">D</span> },
  { name: "HotelTonight",  bg: "#EF4136", mark: <span className="font-display text-[11px] font-extrabold text-white">HT</span> },
  { name: "Hostelworld",   bg: "#F39200", mark: <span className="font-display text-[14px] font-extrabold text-white">H</span> },
  { name: "Kayak",         bg: "#FF690F", mark: <span className="font-display text-[11px] font-extrabold text-white">kyk</span> },
  { name: "Hotels.com",    bg: "#D32F2F", mark: <span className="font-display text-[11px] font-extrabold text-white">h.</span> },
  { name: "Traveloka",     bg: "#0194F3", mark: <span className="font-display text-[11px] font-extrabold text-white">tlk</span> },
  { name: "MakeMyTrip",    bg: "#E73C33", mark: <span className="font-display text-[11px] font-extrabold text-[#FCB813]">MMT</span> },
];

function ChannelChip({ brand }) {
  return (
    <div className="flex shrink-0 items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 backdrop-blur-[2px] transition-colors hover:border-white/20 hover:bg-white/[0.06]">
      <span
        className="grid h-9 w-9 shrink-0 place-items-center rounded-lg ring-1 ring-white/10"
        style={{ background: brand.bg, border: brand.border ? `1px solid ${brand.border}` : undefined }}
      >
        {brand.mark}
      </span>
      <span className="whitespace-nowrap text-[13px] font-semibold text-white/80">{brand.name}</span>
    </div>
  );
}

const FRAGMENTED_SYSTEMS_DEPRECATED = [
  "një sistem për rezervimet",
  "një tjetër për faturimin",
  "një platformë tjetër për Booking.com dhe Airbnb",
  "sisteme të ndara për menaxhimin e dhomave",
  "aplikacione të tjera për Smart Hotel dhe aksesin në dhoma",
  "Excel ose WhatsApp për organizimin e operacioneve të përditshme",
];

const MODULES_DEPRECATED = [
  [Calendar,      "PMS dhe rezervimet"],
  [Globe2,        "Booking Engine"],
  [Repeat2,       "Channel Manager"],
  [Repeat2,       "Sinkronizimi me OTA"],
  [Receipt,       "Faturimi dhe fiskalizimi"],
  [KeyRound,      "Menaxhimi i dhomave"],
  [BarChart3,     "Raportet dhe analizat"],
  [MessageCircle, "Komunikimi me klientin"],
  [Sparkles,      "Integrimet Smart Hotel"],
];

export default function RrethNesh() {
  const t = useT();

  const FRAGMENTED_SYSTEMS = [
    t("një sistem për rezervimet",                                          "one system for bookings"),
    t("një tjetër për faturimin",                                            "another for invoicing"),
    t("një platformë tjetër për Booking.com dhe Airbnb",                     "yet another for Booking.com and Airbnb"),
    t("sisteme të ndara për menaxhimin e dhomave",                           "separate systems for room management"),
    t("aplikacione të tjera për Smart Hotel dhe aksesin në dhoma",            "extra apps for Smart Hotel and room access"),
    t("Excel ose WhatsApp për organizimin e operacioneve të përditshme",     "Excel or WhatsApp to coordinate daily operations"),
  ];

  const MODULES = [
    [Calendar,      t("PMS dhe rezervimet",             "PMS & reservations")],
    [Globe2,        "Booking Engine"],
    [Repeat2,       "Channel Manager"],
    [Repeat2,       t("Sinkronizimi me OTA",             "OTA sync")],
    [Receipt,       t("Faturimi dhe fiskalizimi",        "Billing & fiscalization")],
    [KeyRound,      t("Menaxhimi i dhomave",             "Room management")],
    [BarChart3,     t("Raportet dhe analizat",           "Reports & analytics")],
    [MessageCircle, t("Komunikimi me klientin",          "Guest communication")],
    [Sparkles,      t("Integrimet Smart Hotel",          "Smart Hotel integrations")],
  ];

  return (
    <>
      <SEO
        title={t("Rreth Akomoda", "About Akomoda")}
        description={t(
          "Akomoda është një platformë moderne për menaxhimin e hotelit: PMS, booking engine, channel manager, faturim, komunikim me klientin dhe integrime Smart Hotel — të gjitha në një sistem të vetëm.",
          "Akomoda is a modern hotel management platform: PMS, booking engine, channel manager, billing, guest communication and Smart Hotel integrations — all in one system."
        )}
      />

      {/* ─────────────────────────────────────────────────────── 1. HERO */}
      <section className="relative isolate overflow-hidden bg-[#0F172A] text-white grain-dark" data-testid="rreth-nesh-hero">
        {/* Ambient orange glow */}
        <div className="pointer-events-none absolute -top-40 -right-32 h-[640px] w-[640px] bg-[radial-gradient(closest-side,rgba(255,122,0,0.18),transparent_70%)] blur-3xl" aria-hidden />
        <div className="pointer-events-none absolute -top-20 -left-32 h-[480px] w-[480px] bg-[radial-gradient(closest-side,rgba(30,41,59,0.6),transparent_70%)] blur-3xl" aria-hidden />
        <div className="absolute inset-0 bg-dot-dark opacity-30 mask-fade-y" aria-hidden />

        <div className="container-akm relative pt-28 pb-20 md:pt-40 md:pb-24">
          <div className="max-w-5xl">
            <span className="eyebrow-dark">
              <span className="h-1.5 w-1.5 rounded-full bg-[#FF7A00]" />
              {t("Rreth Akomoda", "About Akomoda")}
            </span>

            <h1
              className="mt-10 font-display font-extrabold tracking-[-0.04em] text-white"
              style={{ fontSize: "clamp(2.25rem, 1.4rem + 4vw, 5rem)", lineHeight: 1.04 }}
              data-testid="rreth-hero-title"
            >
              {t("Një platformë moderne për", "A modern platform for")}<br className="hidden md:inline" />{" "}
              <span className="bg-gradient-to-r from-white via-white to-[#FF7A00] bg-clip-text text-transparent">
                {t("menaxhimin e hotelit.", "hotel management.")}
              </span>
            </h1>

            <div className="mt-12 grid gap-10 md:grid-cols-12">
              <div className="md:col-span-7">
                <div className="space-y-5 text-[16.5px] leading-[1.8] text-white/65 md:text-[17.5px]">
                  <p>
                    {t(
                      "Akomoda është ndërtuar për të lidhur rezervimet, operacionet dhe menaxhimin e hotelit në një sistem të vetëm.",
                      "Akomoda was built to unify reservations, operations and hotel management in a single system."
                    )}
                  </p>
                  <p>
                    {t(
                      "Nga PMS dhe rezervimet, tek booking engine, sinkronizimi me Booking.com dhe Airbnb, faturimi, komunikimi me klientin dhe menaxhimi i operacioneve — gjithçka funksionon bashkë në një platformë të vetme moderne.",
                      "From PMS and reservations to booking engine, Booking.com and Airbnb sync, billing, guest communication and operations management — everything works together in one modern platform."
                    )}
                  </p>
                </div>

                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to="/demo" className="btn-primary !px-6 !py-3 !text-[14.5px]" data-testid="rreth-hero-demo">
                    {t("Kërko Demo", "Request Demo")} <ArrowRight size={15} />
                  </Link>
                  <Link to="/platforma" className="btn-on-dark !px-6 !py-3 !text-[14.5px]">
                    {t("Shiko Platformën", "See the Platform")}
                  </Link>
                </div>
              </div>

              {/* Right side — goal mini-block */}
              <div className="md:col-span-5">
                <div className="relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02] p-6 backdrop-blur-[2px] md:p-7">
                  <div className="pointer-events-none absolute -inset-1 -z-10 rounded-2xl bg-[conic-gradient(from_0deg_at_30%_20%,rgba(255,122,0,0.22),transparent_45%,rgba(255,122,0,0.16)_75%,transparent)] opacity-90 blur-2xl glow-orbit" aria-hidden />
                  <div className="pointer-events-none absolute -inset-1 -z-10 rounded-2xl bg-[radial-gradient(60%_50%_at_20%_15%,rgba(255,176,104,0.18),transparent_70%)] glow-drift" aria-hidden />
                  <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#FFB068]">{t("Qëllimi ynë", "Our mission")}</div>
                  <p className="mt-4 text-[15.5px] leading-[1.7] text-white/80">
                    {t(
                      "T'u japim hoteleve më shumë kontroll, më pak procese manuale dhe një mënyrë më të organizuar për të menaxhuar punën e përditshme.",
                      "Give hotels more control, less manual work and a more organised way to run daily operations."
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Channel logo marquee — sliding right→left */}
        <div className="relative pb-16 md:pb-20">
          <div className="container-akm">
            <div className="text-center">
              <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/45">
                <span className="h-px w-8 bg-white/15" />
                {t("Kanalet kryesore të rezervimeve", "Major booking channels")}
                <span className="h-px w-8 bg-white/15" />
              </span>
            </div>
          </div>

          <div className="relative mt-8 overflow-hidden mask-fade-x">
            <div className="marquee-channels">
              {[...CHANNEL_BRANDS, ...CHANNEL_BRANDS].map((b, i) => (
                <ChannelChip key={i} brand={b} />
              ))}
            </div>
          </div>

          <div className="relative mt-4 overflow-hidden mask-fade-x">
            <div className="marquee-channels-rev">
              {[...CHANNEL_BRANDS_2, ...CHANNEL_BRANDS_2].map((b, i) => (
                <ChannelChip key={i} brand={b} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────── 2. SI LINDI AKOMODA */}
      <section className="bg-[#F8FAFC]">
        <div className="container-tight section-lg">
          {/* Heading — full width, left-aligned with eyebrow */}
          <div className="max-w-3xl">
            <div className="eyebrow">{t("01 — Fillimi", "01 — The Beginning")}</div>
            <h2
              className="mt-5 font-display font-extrabold tracking-[-0.035em] text-[#0F172A]"
              style={{ fontSize: "clamp(1.95rem, 1.2rem + 2.8vw, 3.25rem)", lineHeight: 1.05 }}
            >
              {t("Si lindi Akomoda.", "How Akomoda was born.")}
            </h2>
            <p className="mt-7 max-w-2xl text-[16.5px] leading-[1.8] text-[#64748B] md:text-[17.5px]">
              {t(
                "Akomoda lindi nga problemet reale që po hasnin gjashtë hotelierë, të cilët nuk po arrinin të gjenin një platformë që t'i mbulonte të gjitha proceset në një vend.",
                "Akomoda was born out of the real problems six hoteliers were facing — none of them could find a platform that covered every process in one place."
              )}
            </p>
          </div>

          <div className="mx-auto mt-20 max-w-2xl">
            <div className="text-center text-[11.5px] font-semibold uppercase tracking-[0.18em] text-[#64748B]">
              {t("Ata përdornin", "They were using")}
            </div>

            <ul className="mt-7 space-y-2.5">
              {FRAGMENTED_SYSTEMS.map((tt, i) => (
                <li
                  key={tt}
                  className="group flex items-center gap-3 rounded-lg border border-[#E2E8F0] bg-white px-4 py-3 transition-all hover:border-[#CBD5E1]"
                >
                  <span className="font-mono text-[11px] font-semibold text-[#94A3B8]">{String(i + 1).padStart(2, "0")}</span>
                  <span className="h-px flex-1 bg-[#E2E8F0] group-hover:bg-[#CBD5E1]" aria-hidden />
                  <span className="text-[14px] font-medium text-[#1F2937]">{tt}</span>
                </li>
              ))}
            </ul>

            {/* Vertical connector → Akomoda logo (not text) → connector */}
            <div className="mt-10 flex flex-col items-center">
              <div className="h-12 w-px bg-gradient-to-b from-[#E2E8F0] to-[#FF7A00]/30" />

              <div className="relative my-3">
                <div className="pointer-events-none absolute -inset-10 -z-10 rounded-3xl bg-[radial-gradient(closest-side,rgba(255,122,0,0.28),transparent_70%)] blur-md breathe" aria-hidden />
                <div className="relative flex h-24 w-80 items-center justify-center rounded-2xl border border-[#FF7A00]/20 bg-[#0F172A] px-8 shadow-[0_20px_50px_-10px_rgba(255,122,0,0.55)] md:h-28 md:w-[22rem]">
                  <img
                    src="/akomoda-logo-light.png"
                    alt="Akomoda"
                    className="h-12 w-auto md:h-14"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </div>

              <div className="h-12 w-px bg-gradient-to-b from-[#FF7A00]/30 to-[#0F172A]" />
            </div>

            <div className="rounded-xl border border-[#0F172A] bg-[#0F172A] p-5 text-center text-white md:p-6">
              <div className="text-[11.5px] font-semibold uppercase tracking-[0.16em] text-[#FFB068]">{t("Një platformë e vetme","One single platform")}</div>
              <p className="mx-auto mt-3 max-w-md text-[14.5px] leading-[1.65] text-white/80">
                {t(
                  "Rezervimet, faturimi, kanalet online, dhomat, Smart Hotel dhe komunikimi me klientin — të gjitha të lidhura në një vend.",
                  "Reservations, billing, online channels, rooms, Smart Hotel and guest communication — all connected in one place."
                )}
              </p>
            </div>
          </div>

          {/* Bottom narrative paragraphs */}
          <div className="mx-auto mt-24 max-w-3xl text-center">
            <p className="text-[17px] leading-[1.8] text-[#1F2937]">
              {t("Rezultati ishte gjithmonë i njëjtë:","The result was always the same:")}{" "}
              <span className="font-semibold">
                {t(
                  "shumë procese manuale, mungesë sinkronizimi, rezervime të dyfishta dhe operacione që bëheshin më të komplikuara çdo ditë.",
                  "too much manual work, missing sync, double bookings and operations that got more complicated every day."
                )}
              </span>
            </p>
            <p className="mt-6 text-[16px] leading-[1.8] text-[#64748B]">
              {t(
                "Në vend që teknologjia ta thjeshtonte punën, ajo po krijonte edhe më shumë rrëmujë.",
                "Instead of making things simpler, the tech was creating even more chaos."
              )}
            </p>
            <p className="mt-6 font-display text-[18px] font-bold tracking-[-0.02em] text-[#0F172A] md:text-[20px]">
              {t(
                "Pikërisht nga kjo nevojë reale lindi Akomoda — një platformë moderne ku gjithçka lidhet dhe funksionon bashkë.",
                "Out of that real need, Akomoda was born — a modern platform where everything connects and works together."
              )}
            </p>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────── 3. ÇFARË OFRON AKOMODA */}
      <section className="bg-white">
        <div className="container-akm section-lg">
          <div className="mx-auto max-w-2xl text-center">
            <div className="eyebrow">{t("02 — Platforma", "02 — The Platform")}</div>
            <h2
              className="mt-5 font-display font-extrabold tracking-[-0.035em] text-[#0F172A]"
              style={{ fontSize: "clamp(1.95rem, 1.2rem + 2.8vw, 3.25rem)", lineHeight: 1.05 }}
            >
              {t("Çfarë ofron Akomoda.", "What Akomoda offers.")}
            </h2>
            <p className="mt-6 text-[16px] leading-[1.75] text-[#64748B]">
              {t(
                "Akomoda kombinon nëntë module të lidhura mes tyre në një platformë të vetme.",
                "Akomoda combines nine interconnected modules in one single platform."
              )}
            </p>
          </div>

          {/* Connected ecosystem — central hub with modules orbiting */}
          <div className="relative mx-auto mt-20 max-w-[1080px]">
            <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(45%_45%_at_50%_50%,rgba(255,122,0,0.06),transparent_70%)]" aria-hidden />

            <div className="grid items-center gap-y-12 md:grid-cols-[1fr_280px_1fr]">
              {/* Left modules */}
              <div className="space-y-3">
                {MODULES.slice(0, 4).map(([Icon, label]) => (
                  <div
                    key={label}
                    className="flex items-center justify-end gap-3 rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 transition-all hover:-translate-y-0.5 hover:border-[#CBD5E1]"
                  >
                    <span className="text-[14px] font-semibold text-[#1F2937]">{label}</span>
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-[#E2E8F0] bg-[#F8FAFC]">
                      <Icon size={15} className="text-[#FF7A00]" />
                    </span>
                  </div>
                ))}
              </div>

              {/* Center hub */}
              <div className="relative mx-auto w-full max-w-[260px]">
                <div className="absolute -inset-8 rounded-full bg-[radial-gradient(closest-side,rgba(255,122,0,0.18),transparent)] blur-md breathe" aria-hidden />
                <div className="absolute -inset-3 rounded-3xl border border-[#FF7A00]/15" aria-hidden />
                <div className="relative rounded-2xl border border-[#0F172A] bg-[#0F172A] p-6 text-white shadow-cinematic-dark">
                  <div className="flex items-center justify-between">
                    <img src="/akomoda-logo-light.png" alt="Akomoda" className="h-7 w-auto" loading="lazy" decoding="async" />
                    <span className="inline-flex items-center gap-1.5 rounded-md border border-emerald-400/25 bg-emerald-400/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-300">
                      <span className="h-1 w-1 rounded-full bg-emerald-400 pulse-soft" /> {t("aktiv","active")}
                    </span>
                  </div>
                  <p className="mt-4 text-[13.5px] leading-[1.6] text-white/70">
                    {t(
                      "Një platformë e vetme. Të gjitha modulet të lidhura në kohë reale.",
                      "One single platform. Every module connected in real time."
                    )}
                  </p>
                  <div className="mt-4 grid grid-cols-3 gap-1.5">
                    {Array.from({ length: 9 }).map((_, i) => (
                      <div key={i} className="h-1.5 rounded-full bg-gradient-to-r from-[#FF7A00] to-[#FFB068]" style={{ opacity: 0.4 + i / 18 }} />
                    ))}
                  </div>
                </div>
              </div>

              {/* Right modules */}
              <div className="space-y-3">
                {MODULES.slice(4, 9).map(([Icon, label]) => (
                  <div
                    key={label}
                    className="flex items-center gap-3 rounded-xl border border-[#E2E8F0] bg-white px-4 py-3 transition-all hover:-translate-y-0.5 hover:border-[#CBD5E1]"
                  >
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-[#E2E8F0] bg-[#F8FAFC]">
                      <Icon size={15} className="text-[#FF7A00]" />
                    </span>
                    <span className="text-[14px] font-semibold text-[#1F2937]">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Closing line */}
          <div className="mx-auto mt-20 max-w-2xl text-center">
            <p className="font-display text-[20px] font-bold tracking-[-0.02em] text-[#0F172A] md:text-[24px]">
              {t("Pa sisteme të shpërndara.","No scattered systems.")}<br />
              <span className="text-[#64748B]">{t("Pa procese manuale.","No manual processes.")}</span><br />
              <span className="bg-gradient-to-r from-[#0F172A] to-[#FF7A00] bg-clip-text text-transparent">
                {t("Pa rezervime të dyfishta.","No double bookings.")}
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────── 4. NDËRTUAR PËR HOTELERI MODERNE */}
      <section className="bg-[#F8FAFC]">
        <div className="container-tight section-lg">
          <div className="grid gap-14 md:grid-cols-12">
            <div className="md:col-span-5">
              <div className="eyebrow">{t("03 — Vizioni","03 — The Vision")}</div>
              <h2
                className="mt-5 font-display font-extrabold tracking-[-0.035em] text-[#0F172A]"
                style={{ fontSize: "clamp(1.95rem, 1.2rem + 2.8vw, 3.25rem)", lineHeight: 1.05 }}
              >
                {t("Ndërtuar për","Built for")}<br />{t("hoteleri moderne.","modern hospitality.")}
              </h2>
            </div>

            <div className="md:col-span-7">
              <p className="text-[18px] font-medium leading-[1.5] text-[#0F172A] md:text-[20px]">
                {t("Sot, hoteleria lëviz më shpejt se kurrë.","Today, hospitality moves faster than ever.")}
              </p>

              <div className="mt-8 space-y-5 text-[16px] leading-[1.8] text-[#1F2937]">
                <p className="flex items-start gap-3">
                  <span className="mt-2.5 h-1 w-6 shrink-0 rounded-full bg-[#FF7A00]" aria-hidden />
                  <span>{t("Klientët rezervojnë nga shumë kanale njëkohësisht.","Guests book across many channels at once.")}</span>
                </p>
                <p className="flex items-start gap-3">
                  <span className="mt-2.5 h-1 w-6 shrink-0 rounded-full bg-[#FF7A00]" aria-hidden />
                  <span>{t("Disponueshmëria duhet të përditësohet në kohë reale.","Availability has to update in real time.")}</span>
                </p>
                <p className="flex items-start gap-3">
                  <span className="mt-2.5 h-1 w-6 shrink-0 rounded-full bg-[#FF7A00]" aria-hidden />
                  <span>{t("Rezervimet direkte janë më të rëndësishme.","Direct bookings matter more than ever.")}</span>
                </p>
                <p className="flex items-start gap-3">
                  <span className="mt-2.5 h-1 w-6 shrink-0 rounded-full bg-[#FF7A00]" aria-hidden />
                  <span>{t("Dhe operacionet kërkojnë më shumë organizim dhe automatizim.","And operations demand more structure and automation.")}</span>
                </p>
              </div>

              <div className="mt-10 border-l-2 border-[#FF7A00] pl-5">
                <p className="text-[15px] text-[#64748B]">
                  {t("Akomoda është ndërtuar për këtë realitet të ri.","Akomoda is built for this new reality.")}
                </p>
                <p className="mt-2 font-display text-[18px] font-bold tracking-[-0.02em] text-[#0F172A]">
                  {t("Një platformë moderne për hotele moderne.","A modern platform for modern hotels.")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────── 5. MË PAK SISTEME. MË SHUMË KONTROLL. */}
      <section className="relative overflow-hidden bg-[#0F172A] text-white grain-dark">
        <div className="pointer-events-none absolute -top-32 left-1/2 h-[560px] w-[1100px] -translate-x-1/2 bg-[radial-gradient(closest-side,rgba(255,122,0,0.18),transparent)] blur-3xl" aria-hidden />
        <div className="absolute inset-0 bg-dot-dark opacity-25 mask-fade-y" aria-hidden />

        <div className="container-tight relative section-lg">
          <div className="mx-auto max-w-4xl text-center">
            <div className="eyebrow-dark">
              <span className="h-1.5 w-1.5 rounded-full bg-[#FF7A00]" />
              {t("04 — Besimi ynë", "04 — Our Belief")}
            </div>

            <h2
              className="mt-8 font-display font-extrabold tracking-[-0.045em] text-white"
              style={{ fontSize: "clamp(2.25rem, 1.3rem + 3.8vw, 4.75rem)", lineHeight: 1.02 }}
            >
              {t("Më pak sisteme.", "Fewer systems.")}<br />
              <span className="bg-gradient-to-r from-white via-white to-[#FF7A00] bg-clip-text text-transparent">
                {t("Më shumë kontroll.", "More control.")}
              </span>
            </h2>

            <div className="mx-auto mt-10 max-w-2xl space-y-5 text-[16.5px] leading-[1.8] text-white/65 md:text-[17.5px]">
              <p>
                {t("Ne besojmë që hotelet nuk kanë nevojë për më shumë programe të ndara.", "We believe hotels don't need more disconnected apps.")}
              </p>
              <p>
                {t("Kanë nevojë për një platformë që funksionon qartë, shpejt dhe pa komplikime.", "They need a platform that works clearly, quickly and without friction.")}
              </p>
              <p className="text-white/80">
                {t("Një platformë ku rezervimet, operacionet dhe përvoja e klientit punojnë bashkë.", "A platform where reservations, operations and guest experience all work together.")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────────────────── 6. FINAL CTA */}
      <section className="relative overflow-hidden bg-[#080D1A] text-white">
        <div className="pointer-events-none absolute -top-32 left-1/2 h-[440px] w-[900px] -translate-x-1/2 bg-[radial-gradient(closest-side,rgba(255,122,0,0.16),transparent)] blur-3xl" aria-hidden />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/15 to-transparent" aria-hidden />

        <div className="container-tight relative py-20 md:py-24">
          <div className="mx-auto max-w-3xl text-center">
            <h3
              className="font-display font-extrabold tracking-[-0.035em] text-white"
              style={{ fontSize: "clamp(1.75rem, 1.1rem + 2.4vw, 2.75rem)", lineHeight: 1.1 }}
            >
              {t("Gati për ta provuar Akomoda","Ready to try Akomoda")}
              <span className="bg-gradient-to-r from-white via-white to-[#FF7A00] bg-clip-text text-transparent"> {t("në hotelin tuaj?","in your hotel?")}</span>
            </h3>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
              <Link to="/demo" className="btn-primary !px-7 !py-3.5 !text-[15px]" data-testid="rreth-final-demo">
                {t("Kërko Demo","Request Demo")} <ArrowRight size={16} />
              </Link>
              <Link to="/kontakt" className="btn-on-dark !px-7 !py-3.5 !text-[15px]" data-testid="rreth-final-contact">
                {t("Na Kontaktoni","Contact us")}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
